import { useState } from 'react';
import type { ScanDto } from '@compflash/contracts';
import { describeCondition, formatDays, formatMoney } from '../lib/format';

type Valuation = NonNullable<ScanDto['valuation']>;

/**
 * Comparable sales table.
 *
 * Two rules that come from the data-licensing research:
 *  1. Excluded comps are shown with their reason. If we silently dropped sales,
 *     a user could not audit the number we are giving them.
 *  2. Individual records are only rendered when the source licence permits it;
 *     otherwise the source contributes to aggregates only.
 */
export function CompsTable({ valuation }: { valuation: Valuation }): JSX.Element {
  const [showExcluded, setShowExcluded] = useState(false);

  const restrictedProviders = new Set(
    valuation.dataSources.filter((source) => !source.displayIndividualRecords).map((source) => source.provider),
  );

  const included = valuation.comps.filter((comp) => comp.included);
  const excluded = valuation.comps.filter((comp) => !comp.included);
  const rows = showExcluded ? [...included, ...excluded] : included;

  return (
    <div className="comps">
      {restrictedProviders.size > 0 && (
        <p className="notice notice-info small">
          {[...restrictedProviders].join(', ')} is licensed for aggregate display only, so individual sales from that
          source are counted in the statistics but not listed.
        </p>
      )}
      <table className="table">
        <thead>
          <tr>
            <th>Sold</th>
            <th>Title</th>
            <th>Condition</th>
            <th className="num">Price</th>
            <th className="num">Total</th>
            <th className="num">Used as</th>
            <th className="num">Match</th>
            <th>Source</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((comp) => {
            const hidden = restrictedProviders.has(comp.sale.provenance.provider);
            return (
              <tr key={comp.sale.id} className={comp.included ? undefined : 'row-excluded'}>
                <td>{formatDays(comp.ageDays)}</td>
                <td className="title">
                  {hidden ? <span className="muted">Hidden by source licence</span> : comp.sale.title}
                  {comp.gradeAdjustmentNote && <div className="muted small">{comp.gradeAdjustmentNote}</div>}
                  {!comp.included && <div className="muted small">Excluded: {comp.exclusionReason?.replace(/_/g, ' ')}</div>}
                </td>
                <td>{describeCondition(comp.sale.condition as never)}</td>
                <td className="num">{hidden ? '—' : formatMoney(comp.sale.price)}</td>
                <td className="num">{hidden ? '—' : formatMoney(comp.sale.total)}</td>
                <td className="num">{formatMoney(comp.adjustedTotal)}</td>
                <td className="num">{Math.round(comp.matchScore * 100)}%</td>
                <td>
                  {comp.sale.provenance.displayUrlAllowed && comp.sale.provenance.sourceUrl ? (
                    <a href={comp.sale.provenance.sourceUrl} target="_blank" rel="noreferrer noopener">
                      {comp.sale.marketplace}
                    </a>
                  ) : (
                    comp.sale.marketplace
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {excluded.length > 0 && (
        <button type="button" className="ghost" onClick={() => setShowExcluded((value) => !value)}>
          {showExcluded ? 'Hide' : 'Show'} {excluded.length} excluded sale{excluded.length === 1 ? '' : 's'}
        </button>
      )}
    </div>
  );
}
